/**
 * CategoryForm — dialog for creating and editing a layout category.
 *
 * - Accent colour: native <input type="color"> with hex preview
 * - Icon: searchable MUI icon grid picker (stores "mui:IconName")
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { makeStyles } from 'tss-react/mui';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Autocomplete from '@mui/material/Autocomplete';
import { Search as SearchIcon } from '@mui/icons-material';
import ClearIcon from '@mui/icons-material/Clear';
import CategoryIcon from '@mui/icons-material/Category';
import { ICONS, SVG_ICONS, SVG_ICON_PATHS } from '../../iconRegistry';
import type { LayoutCategory, LayoutSection, CreateCategoryInput, UpdateCategoryInput } from '../../types';

// Local lookup map derived from the shared icon registry
const ICON_MAP = Object.fromEntries(ICONS.map(e => [e.key, e]));

// ── Styles ────────────────────────────────────────────────────────────────────

const useStyles = makeStyles()((theme) => ({
  field: { marginBottom: theme.spacing(2) },
  hint: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(-1.5),
    marginBottom: theme.spacing(2),
  },
  // Colour row
  colorRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  colorInput: {
    width: 44,
    height: 44,
    padding: 3,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    cursor: 'pointer',
    background: 'none',
    flexShrink: 0,
    '&::-webkit-color-swatch-wrapper': { padding: 0 },
    '&::-webkit-color-swatch': { borderRadius: 2, border: 'none' },
    '&::-moz-color-swatch': { borderRadius: 2, border: 'none' },
  },
  colorHex: {
    fontSize: 13,
    fontFamily: 'monospace',
    color: theme.palette.text.secondary,
    userSelect: 'all' as const,
  },
  // Icon picker trigger
  iconTrigger: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0.75, 1.5),
    cursor: 'pointer',
    marginBottom: theme.spacing(0.75),
    transition: 'border-color 0.15s',
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
  },
  iconPreview: {
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.08)'
        : theme.palette.grey[100],
    flexShrink: 0,
    color: theme.palette.text.primary,
  },
  iconLabel: { flex: 1, fontSize: 13, color: theme.palette.text.secondary },
  // Icon grid popover
  iconPopover: { padding: theme.spacing(1.5) },
  iconGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(8, 40px)',
    gap: theme.spacing(0.5),
    maxHeight: 280,
    overflowY: 'auto',
    marginTop: theme.spacing(1),
  },
  iconCell: {
    width: 40,
    height: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.shape.borderRadius,
    cursor: 'pointer',
    border: '2px solid transparent',
    color: theme.palette.text.primary,
    '&:hover': { backgroundColor: theme.palette.action.hover },
  },
  iconCellSelected: {
    border: `2px solid ${theme.palette.primary.main}`,
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(244,121,32,0.15)'
        : 'rgba(230,0,126,0.08)',
  },
}));

// ── Icon picker popover ───────────────────────────────────────────────────────

function IconPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const { classes } = useStyles();
  const triggerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const isSvg = SVG_ICON_PATHS.has(value);
  const currentKey = !isSvg && value.startsWith('mui:') ? value.slice(4) : '';
  const currentMuiEntry = currentKey ? ICON_MAP[currentKey] : undefined;
  const currentSvgEntry = isSvg ? SVG_ICONS.find(s => s.path === value) : undefined;

  const q = search.trim().toLowerCase();

  const filteredMui = useMemo(
    () =>
      q
        ? ICONS.filter(
            e =>
              e.label.toLowerCase().includes(q) ||
              e.key.toLowerCase().includes(q),
          )
        : ICONS,
    [q],
  );

  const filteredSvg = useMemo(
    () =>
      q
        ? SVG_ICONS.filter(s => s.label.toLowerCase().includes(q))
        : SVG_ICONS,
    [q],
  );

  const select = (v: string) => {
    onChange(v);
    setOpen(false);
    setSearch('');
  };

  const hasValue = !!currentMuiEntry || !!currentSvgEntry;

  return (
    <>
      <div
        className={classes.iconTrigger}
        ref={triggerRef}
        onClick={() => setOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={e => e.key === 'Enter' && setOpen(true)}
      >
        <div className={classes.iconPreview}>
          {/* eslint-disable no-nested-ternary */}
          {currentMuiEntry ? (
            <currentMuiEntry.Component style={{ fontSize: 20 }} />
          ) : currentSvgEntry ? (
            <img
              src={currentSvgEntry.path}
              alt={currentSvgEntry.label}
              style={{ width: 20, height: 20, objectFit: 'contain' }}
            />
          ) : (
            <CategoryIcon style={{ fontSize: 18, opacity: 0.35 }} />
          )}
          {/* eslint-enable no-nested-ternary */}
        </div>
        <span className={classes.iconLabel}>
          {currentMuiEntry?.label ?? currentSvgEntry?.label ?? 'Choose an icon…'}
        </span>
        {hasValue && (
          <Tooltip title="Clear icon" arrow>
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation();
                onChange('');
              }}
            >
              <ClearIcon style={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
      </div>

      <Popover
        open={open}
        anchorEl={triggerRef.current}
        onClose={() => { setOpen(false); setSearch(''); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <div className={classes.iconPopover} style={{ width: 380 }}>
          <TextField
            size="small"
            variant="outlined"
            fullWidth
            placeholder="Search icons…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ fontSize: 16 }} />
                </InputAdornment>
              ),
            }}
          />

          <div className={classes.iconGrid}>
            {filteredSvg.map(svgIcon => (
              <Tooltip key={svgIcon.path} title={svgIcon.label} arrow placement="top">
                <div
                  className={`${classes.iconCell} ${value === svgIcon.path ? classes.iconCellSelected : ''}`}
                  onClick={() => select(svgIcon.path)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => e.key === 'Enter' && select(svgIcon.path)}
                >
                  <img
                    src={svgIcon.path}
                    alt={svgIcon.label}
                    style={{ width: 20, height: 20, objectFit: 'contain' }}
                  />
                </div>
              </Tooltip>
            ))}
            {filteredMui.map(entry => (
              <Tooltip key={entry.key} title={entry.label} arrow placement="top">
                <div
                  className={`${classes.iconCell} ${currentKey === entry.key ? classes.iconCellSelected : ''}`}
                  onClick={() => select(`mui:${entry.key}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => e.key === 'Enter' && select(`mui:${entry.key}`)}
                >
                  <entry.Component style={{ fontSize: 20 }} />
                </div>
              </Tooltip>
            ))}
            {filteredMui.length === 0 && filteredSvg.length === 0 && (
              <Typography
                variant="body2"
                style={{ gridColumn: '1 / -1', padding: 16, opacity: 0.5, textAlign: 'center' }}
              >
                No icons match.
              </Typography>
            )}
          </div>
        </div>
      </Popover>
    </>
  );
}

// ── CategoryForm ──────────────────────────────────────────────────────────────

interface CategoryFormProps {
  open: boolean;
  existing?: LayoutCategory;
  /** Pre-select section when creating inside a section. Ignored when editing. */
  defaultSectionId?: string;
  /** Available sections to choose from. */
  sections?: LayoutSection[];
  onSave: (data: CreateCategoryInput | UpdateCategoryInput) => Promise<void>;
  onClose: () => void;
}

export function CategoryForm({
  open,
  existing,
  defaultSectionId,
  sections = [],
  onSave,
  onClose,
}: CategoryFormProps) {
  const { classes } = useStyles();
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [accentColor, setAccentColor] = useState('#E6007E');
  const [icon, setIcon] = useState('');

  // Populate form when opening
  useEffect(() => {
    if (open) {
      setName(existing?.name ?? '');
      setDescription(existing?.description ?? '');
      setSectionId(existing?.sectionId ?? defaultSectionId ?? '');
      setAccentColor(existing?.accentColor ?? '#E6007E');
      setIcon(existing?.icon ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existing]); // defaultSectionId intentionally excluded — only applied on initial open

  const isValid = name.trim().length > 0;

  async function handleSave() {
    if (!isValid) return;
    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        description: description.trim() || undefined,
        section_id: sectionId || undefined,
        accent_color: accentColor || undefined,
        icon: icon.trim() || undefined,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{existing ? 'Edit Category' : 'New Category'}</DialogTitle>
      <DialogContent>
        {/* Name */}
        <TextField
          className={classes.field}
          label="Name"
          value={name}
          onChange={e => setName(e.target.value)}
          fullWidth
          required
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          variant="outlined"
          size="small"
        />

        {/* Description */}
        <TextField
          className={classes.field}
          label="Description"
          value={description}
          onChange={e => setDescription(e.target.value)}
          fullWidth
          multiline
          rows={2}
          variant="outlined"
          size="small"
        />

        {/* Section */}
        <Autocomplete
          options={sections}
          value={sections.find(s => s.id === sectionId) ?? null}
          getOptionLabel={s => s.name}
          onChange={(_e, val) => setSectionId(val?.id ?? '')}
          renderInput={params => (
            <TextField
              {...params}
              className={classes.field}
              label="Section"
              variant="outlined"
              size="small"
              placeholder="Select a section"
            />
          )}
        />
        <Typography className={classes.hint}>
          Categories in the same section are grouped together on the layout page.
        </Typography>

        {/* Accent colour */}
        <Typography
          variant="caption"
          style={{ display: 'block', marginBottom: 6, color: 'inherit', opacity: 0.7 }}
        >
          Accent Colour
        </Typography>
        <div className={classes.colorRow}>
          <input
            type="color"
            className={classes.colorInput}
            value={accentColor}
            onChange={e => setAccentColor(e.target.value)}
          />
          <Box>
            <Typography className={classes.colorHex}>{accentColor}</Typography>
            <Typography style={{ fontSize: 11, opacity: 0.5 }}>
              Click the swatch to open the picker
            </Typography>
          </Box>
        </div>

        {/* Icon picker */}
        <Typography
          variant="caption"
          style={{ display: 'block', marginBottom: 6, color: 'inherit', opacity: 0.7 }}
        >
          Icon
        </Typography>
        <IconPicker value={icon} onChange={setIcon} />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={!isValid || saving}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
